import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarHeart, Plus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import { useAuth } from '../../context/AuthContext';
import { EVENT_ERRORS, getEventItems, listEvents, saveEvent, updateEvent } from '../../api/events';
import EventCard from './EventCard';
import EventFormDrawer from './EventFormDrawer';
import { eventWindow } from './eventWindow';
import styles from './Events.module.css';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live now' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'ended', label: 'Ended' },
  { key: 'inactive', label: 'Inactive' },
];

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [loadingEditId, setLoadingEditId] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listEvents(user.idToken);
      setEvents(list);
    } catch (err) {
      setError(err.message || 'Could not load events.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filtered = useMemo(() => {
    if (tab === 'all') return events;
    return events.filter((e) => eventWindow(e) === tab);
  }, [events, tab]);

  const openAdd = () => {
    setEditing(null);
    setSaveError('');
    setDrawerOpen(true);
  };

  const openEdit = async (event) => {
    setSaveError('');
    setLoadingEditId(event.id);
    try {
      let itemIds = event.itemIds || [];
      try {
        itemIds = await getEventItems(event.id, user.idToken);
      } catch {
        // Fall back to list payload when items endpoint is unavailable.
      }
      setEditing({ ...event, itemIds });
      setDrawerOpen(true);
    } catch (err) {
      setError(err.message || 'Could not load this event.');
    } finally {
      setLoadingEditId(null);
    }
  };

  const handleSave = async (data) => {
    setSaving(true);
    setSaveError('');
    try {
      const saved = await saveEvent(data, user.idToken, {
        editingId: editing?.id,
        previousItemIds: editing?.itemIds || [],
      });
      setEvents((prev) => {
        const exists = prev.some((e) => e.id === saved.id);
        if (exists) return prev.map((e) => (e.id === saved.id ? saved : e));
        return [saved, ...prev];
      });
      setDrawerOpen(false);
      setEditing(null);
      await loadEvents();
    } catch (err) {
      setSaveError(EVENT_ERRORS[err.message] || err.message || 'Could not save this event.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (event) => {
    setTogglingId(event.id);
    setError('');
    try {
      const nextStatus = event.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const updated = await updateEvent(event.id, { ...event, status: nextStatus }, user.idToken);
      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, ...updated } : e)));
    } catch (err) {
      setError(err.message || 'Could not update this event.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Catalog"
        title="Festival events"
        subtitle="Time-boxed storefront lists. Products stay in their aisle category — attach them here for the event collection and optional percent pricing."
        actions={<Button icon={Plus} onClick={openAdd}>Create event</Button>}
      />

      <div className={styles.filterRow}>
        <div className={styles.filterTabs}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.filterTab} ${tab === key ? styles.filterTabActive : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading events…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load events"
          description={error}
          action={<Button variant="outline" onClick={loadEvents}>Retry</Button>}
        />
      )}

      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          icon={CalendarHeart}
          title={events.length === 0 ? 'No events yet' : 'No events in this filter'}
          description={events.length === 0 ? 'Create a festival event to feature a product list on the customer home.' : 'Try another tab.'}
          action={events.length === 0 ? <Button icon={Plus} onClick={openAdd}>Create event</Button> : null}
        />
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className={styles.grid}>
          {filtered.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onEdit={openEdit}
              onToggle={toggleStatus}
              toggling={togglingId === event.id}
              editing={loadingEditId === event.id}
            />
          ))}
        </div>
      )}

      <EventFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null); }}
        onSave={handleSave}
        event={editing}
        saving={saving}
        error={saveError}
      />
    </div>
  );
}
